import { FC } from 'react';

import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftDeliverySummary } from '@shared/api/giftDelivery/giftDelivery.interface';
import {
  GiftOrderForAdmin,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { formatDate } from '@shared/utils/formatDate';
import { formatPrice } from '@shared/utils/formatPrice';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import {
  getCouponStats,
  getDeliveryStats,
  getOrderType,
  paymentStatusLabel,
} from '../utils/giftOrder.constants';
import { DeliveryModeBadge, OrderTypeBadge } from './OrderBadges';

export const CouponStatsCell: FC<{
  order: GiftOrderForAdmin;
  onCouponClick: (coupons: GiftCouponSummary[]) => void;
}> = ({ order, onCouponClick }) => {
  const couponStats = getCouponStats(order);
  if (!couponStats || !order.coupons || order.coupons.length === 0) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  return (
    <button
      onClick={() => onCouponClick(order.coupons ?? [])}
      className="cursor-pointer transition-opacity hover:opacity-80"
    >
      <span className="inline-flex items-center gap-0.5 text-xs">
        <span className="font-semibold text-gray-700">
          {couponStats.canceledCount > 0 ? (
            <>
              {couponStats.activeCount}
              <span className="text-[10px] font-normal text-red-400">
                ({couponStats.totalCount}-{couponStats.canceledCount})
              </span>
            </>
          ) : (
            couponStats.totalCount
          )}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            couponStats.notSharedCount > 0 ? 'text-orange-600' : 'text-gray-400'
          }`}
        >
          {couponStats.notSharedCount}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            couponStats.unusedCount > 0 ? 'text-blue-600' : 'text-gray-400'
          }`}
        >
          {couponStats.unusedCount}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            couponStats.deliveredCount > 0 ? 'text-green-600' : 'text-gray-400'
          }`}
        >
          {couponStats.deliveredCount}
        </span>
      </span>
    </button>
  );
};

export const DeliveryStatsCell: FC<{
  order: GiftOrderForAdmin;
  onDeliveryClick: (deliveries: GiftDeliverySummary[]) => void;
}> = ({ order, onDeliveryClick }) => {
  const deliveryStats = getDeliveryStats(order);
  if (!deliveryStats) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  return (
    <button
      onClick={() => onDeliveryClick((order.deliveries ?? []) as GiftDeliverySummary[])}
      className="cursor-pointer transition-opacity hover:opacity-80"
    >
      <span className="inline-flex items-center gap-0.5 text-xs">
        <span className="font-semibold text-gray-700">
          {deliveryStats.canceledCount > 0 ? (
            <>
              {deliveryStats.activeCount}
              <span className="text-[10px] font-normal text-red-400">
                ({deliveryStats.totalCount}-{deliveryStats.canceledCount})
              </span>
            </>
          ) : (
            deliveryStats.totalCount
          )}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            deliveryStats.waitingAddressCount > 0 ? 'text-orange-600' : 'text-gray-400'
          }`}
        >
          {deliveryStats.waitingAddressCount}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            deliveryStats.preparingCount > 0 ? 'text-yellow-600' : 'text-gray-400'
          }`}
        >
          {deliveryStats.preparingCount}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            deliveryStats.shippingCount > 0 ? 'text-blue-600' : 'text-gray-400'
          }`}
        >
          {deliveryStats.shippingCount}
        </span>
        <span className="text-gray-300">/</span>
        <span
          className={`font-semibold ${
            deliveryStats.deliveredCount > 0 ? 'text-green-600' : 'text-gray-400'
          }`}
        >
          {deliveryStats.deliveredCount}
        </span>
      </span>
    </button>
  );
};

export const OrderRow: FC<{
  order: GiftOrderForAdmin;
  onCouponClick: (coupons: GiftCouponSummary[]) => void;
  onDeliveryClick: (deliveries: GiftDeliverySummary[]) => void;
}> = ({ order, onCouponClick, onDeliveryClick }) => {
  const memberName = order.bugo?.member?.fullName || '-';
  const memberPhone = order.bugo?.member?.phoneNumber || '-';
  const deceasedName = order.bugo?.fevent?.deceasedInfo?.name || '-';
  const bugoBrandName =
    typeof order.bugoBrand === 'object' ? order.bugoBrand.bugoAgency?.corpName : '-';
  const totalItems = order.orderDetails.reduce((sum, d) => sum + d.quantity, 0);
  const firstItemName = order.orderDetails[0]?.giftItemEmbed.name || '-';
  const orderName =
    order.orderDetails.length > 1
      ? `${firstItemName} 외 ${order.orderDetails.length - 1}건`
      : firstItemName;
  const orderType = getOrderType(order);

  return (
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {formatDate(order.paymentDetail.purchasedAt || order.createdAt)}
      </td>
      <td className="px-3 py-4 text-sm">
        <p className="font-medium text-indigo-600 hover:text-indigo-900">{orderName}</p>
        <div className="flex items-center gap-2">
          <p className="text-xs text-gray-400">수량: {totalItems}개</p>
          {order.paymentDetail.receiptUrl && (
            <button
              type="button"
              className="ml-1 rounded border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-200"
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  order.paymentDetail.receiptUrl,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
            >
              영수증
            </button>
          )}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <div className="flex items-center gap-1">
          <OrderTypeBadge orderType={orderType} />
          {(orderType === 'delivery' || orderType === 'mixed') && (
            <DeliveryModeBadge mode={order.deliveryMode} />
          )}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <p className="text-gray-900">{formatPrice(order.paymentDetail.paymentByCash)}원</p>
        {(order.paymentDetail.cancelledTotalPrice ?? 0) > 0 && (
          <p className="text-xs text-red-500">
            -{formatPrice(order.paymentDetail.cancelledTotalPrice ?? 0)}원 취소
          </p>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <p>{memberName}</p>
        <p className="text-xs">{phoneNumberStringFormator(memberPhone)}</p>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {deceasedName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {bugoBrandName}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <CouponStatsCell order={order} onCouponClick={onCouponClick} />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <DeliveryStatsCell order={order} onDeliveryClick={onDeliveryClick} />
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm">
        <span
          className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
            order.paymentDetail.status === GiftOrderPaymentStatus.Paid ||
            order.paymentDetail.status === GiftOrderPaymentStatus.Confirmed
              ? 'bg-green-100 text-green-800'
              : order.paymentDetail.status === GiftOrderPaymentStatus.Canceled
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {paymentStatusLabel[order.paymentDetail.status] || order.paymentDetail.status}
        </span>
      </td>
    </tr>
  );
};
